import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Dropdown } from 'react-bootstrap';
import { FaSpinner, FaExclamationTriangle, FaTable, FaSync, FaDownload, FaInfoCircle, FaEdit, FaTimes, FaSort, FaSortUp, FaSortDown, FaFileCsv, FaFileExcel } from 'react-icons/fa';
import DatasetFilterPanel from '../virtual-lab/DatasetFilterPanel';
import ReviewImage from '../common/ReviewImage';
import useDatasetData from '../../hooks/useDatasetData';
import apiClient from '../../services/apiClient';
import { useNotification } from '../framework/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import DetailedCharts from '../preview/DetailedCharts'; // Assuming we might want to show this
import DatasetSummaryStats from '../preview/DatasetSummaryStats';

import { processDatasetRawData, STRICT_CONFIG_MASTER, STRICT_CONFIG_USER } from '../../utils/datasetUtils';
import { exportToCSV, exportToExcel } from '../../utils/datasetExport';



import { useDatasetFiltering } from '../../hooks/useDatasetFiltering';

const EMPTY_VERSIONS = [];

const DatasetExplorer = ({
    sourceId = 'main',
    data = null, // Optional partial data (e.g. from My Submissions details)
    showActions = false,
    onEdit = null,
    showStatus = false,
    enableVersioning = true,
    visualizations = false,
    customTitle = null,
    onVersionSaved = null,
    preProcessed = false,
    showFilters = true
}) => {
    // 1. Data Fetching / Handling
    // If 'data' prop is provided, we use it directly (must process if raw).
    // If NOT provided, we use the hook.
    // fix: Pass EMPTY_VERSIONS to avoid infinite loop due to new array reference on every render.
    // fix: Pass 'SKIP' if data is present to prevent fetching 'main'
    const { flattenedData: hookData, loading: hookLoading, error: hookError, metadata: hookMetadata, refresh } = useDatasetData(data ? 'SKIP' : sourceId, EMPTY_VERSIONS);

    const [flattenedData, setFlattenedData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [metadata, setMetadata] = useState({});

    useEffect(() => {
        if (data) {
            // Process passed data
            const config = showStatus ? STRICT_CONFIG_USER : STRICT_CONFIG_MASTER;
            const processed = preProcessed ? data : processDatasetRawData(data, config);
            // If showStatus is true, we might want mapped status.
            setFlattenedData(processed);
            setLoading(false);
            setError(null); // Clear any previous errors
            setMetadata({ name: customTitle || (showStatus ? 'Provisional Data' : 'Dataset View'), isPending: showStatus });
        } else {
            setFlattenedData(hookData);
            setLoading(hookLoading);
            setError(hookError);
            setMetadata(hookMetadata);
        }
    }, [data, hookData, hookLoading, hookError, hookMetadata, customTitle, showStatus, preProcessed]);


    // 2. Local State
    const [filterConfig, setFilterConfig] = useState({});
    const [visibleCount, setVisibleCount] = useState(50);
    const scrollContainerRef = useRef(null);
    const { success, error: notifyError } = useNotification();
    const { user } = useAuth(); // Still needed for auth context if any, but filtering hook handles user ID check.

    // Save Version State
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [versionName, setVersionName] = useState('');
    const [saving, setSaving] = useState(false);
    const [excludedIds, setExcludedIds] = useState(new Set()); // Track manually excluded IDs

    // Column sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // 3. Filtering Logic (Using Hook)
    const filteredData = useDatasetFiltering(flattenedData, filterConfig);

    // 3b. Column sorting (applied on top of the advanced filters above)
    const displayData = useMemo(() => {
        if (!sortConfig.key) return filteredData;
        const { key, direction } = sortConfig;
        const sorted = [...filteredData].sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            const an = parseFloat(av);
            const bn = parseFloat(bv);
            const bothNumeric = av !== '-' && bv !== '-' && !isNaN(an) && !isNaN(bn);
            let cmp;
            if (bothNumeric) {
                cmp = an - bn;
            } else {
                cmp = String(av ?? '').localeCompare(String(bv ?? ''));
            }
            return direction === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [filteredData, sortConfig]);

    const requestSort = (key) => {
        setSortConfig(prev => {
            if (prev.key !== key) return { key, direction: 'asc' };
            if (prev.direction === 'asc') return { key, direction: 'desc' };
            return { key: null, direction: 'asc' };
        });
    };

    // Reset scroll on data change
    useEffect(() => {
        setVisibleCount(50);
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
        // Optionally reset excluded IDs when filters change?
        // setExcludedIds(new Set());
        // Decision: KEEP excluded IDs even if they are filtered out temporarily.
    }, [displayData]);

    // Init filters from metadata if available (for loading saved versions)
    useEffect(() => {
        if (metadata && metadata.filterConfig) {
            setFilterConfig(metadata.filterConfig);
        }
    }, [metadata]);


    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
            if (visibleCount < displayData.length) {
                setVisibleCount(prev => Math.min(prev + 50, displayData.length));
            }
        }
    };

    const toggleExclusion = (id) => {
        setExcludedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSaveVersion = async () => {
        setSaving(true);
        try {
            // Filter out excluded records
            const finalDataset = displayData.filter(row => {
                const id = row['Experiment Id'] || row.experimentId || row.recordId || row.id;
                return !excludedIds.has(id);
            });

            const payload = {
                name: versionName,
                filterConfig: JSON.stringify(filterConfig),
                datasetSnapshot: JSON.stringify(finalDataset),
                recordCount: finalDataset.length,
                isProvisional: !!(metadata && metadata.isPending)
            };
            await apiClient.post('/versions', payload);
            success('Version saved successfully!');
            setShowSaveModal(false);
            setVersionName('');
            setExcludedIds(new Set()); // Reset exclusion after save
            if (onVersionSaved) onVersionSaved();
        } catch (err) {
            console.error(err);
            notifyError('Failed to save version.');
        } finally {
            setSaving(false);
        }
    };

    const handleExport = (format) => {
        const exportHeaders = headers.filter(h => h !== 'Actions');
        const rows = displayData.filter(row => {
            const id = row['Experiment Id'] || row.experimentId || row.recordId || row.id;
            return !excludedIds.has(id);
        });
        const baseName = (customTitle || metadata.name || 'dataset').toString().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'dataset';
        try {
            if (format === 'csv') exportToCSV({ rows, headers: exportHeaders, filename: `${baseName}_export.csv` });
            else if (format === 'excel') exportToExcel({ rows, headers: exportHeaders, filename: `${baseName}_export.xlsx` });
        } catch (err) {
            console.error(err);
            notifyError('Failed to export the current view.');
        }
    };


    // Columns Definition
    // We can infer columns from the first row of filtered data or use a strict config.
    // Existing DatasetOverview used STRICT_CONFIG. processData in useDatasetData also uses STRICT_CONFIG.
    // So the data coming in is already normalized to those keys.
    // We just need to extract headers.
    const headers = useMemo(() => {
        // Infer columns from the unfiltered data so the header row (and "no results"
        // messaging) still renders correctly when filters/search match zero rows.
        const columnSource = flattenedData.length > 0 ? flattenedData : displayData;
        let cols = [];
        if (columnSource.length > 0) {
            // Filter out internal keys like _userId, _originalIndex if any
            cols = Object.keys(columnSource[0]).filter(k => k !== 'actions' && k !== '_userId' && k !== 'recordId');
        } else {
            // Fallback default headers if empty? Or just empty.
            return [];
        }

        // Custom Sort/Order could be applied here if needed. 
        // For now, Object.keys logic from processData should be consistent.
        // We might want to remove 'Status' and 'Images' if not requested, but processData might have added them.

        if (!showStatus) {
            cols = cols.filter(c => c !== 'Status');
        }

        // Ensure Images is there? It usually is.

        if (showActions) {
            return ['Actions', ...cols];
        }
        return cols;

    }, [flattenedData, displayData, showActions, showStatus]);


    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5 h-100">
                <FaSpinner className="spinner-border text-primary" role="status" />
                <span className="ms-2">Loading dataset...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger m-3 d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                <div>{error}</div>
            </div>
        );
    }

    if (!filteredData || (filteredData.length === 0 && !Object.keys(filterConfig).length)) {
        return (
            <div className="text-center py-5 text-muted">
                <p>No data available in this dataset.</p>
                <div className="mt-3">
                    <button className="btn btn-outline-primary btn-sm" onClick={refresh}>
                        <FaSync className="me-1" /> Refresh
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="dataset-explorer d-flex flex-column h-100">
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-3 p-3 border-bottom bg-white">
                <div>
                    <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                        <FaTable className="me-2 text-primary" />
                        {customTitle ? customTitle : (metadata.name || 'Dataset Explorer')}
                    </h5>
                    {metadata.isPending && <small className="text-muted">Provisional submissions</small>}
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-dark border me-2">
                        {displayData.length} Records {(Object.keys(filterConfig).some(k => filterConfig[k] && filterConfig[k].length)) && '(Filtered)'}
                        {excludedIds.size > 0 && <span className="text-danger ms-1">({excludedIds.size} excluded)</span>}
                    </span>

                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={refresh}
                        title="Refresh Data"
                    >
                        <FaSync />
                    </button>

                    {enableVersioning && (metadata.isMain || metadata.isPending) && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Save current filters as a version"
                            onClick={() => setShowSaveModal(true)}
                        >
                            <FaDownload className="me-1" /> Save Version
                        </button>
                    )}
                </div>
            </div>

            {/* Dataset Summary + Visualizations (Optional) */}
            {visualizations && (
                <div className="px-3 pb-3 pt-3">
                    <DatasetSummaryStats data={displayData} totalUnfiltered={flattenedData.length} />
                    <DetailedCharts data={displayData} />
                </div>
            )}

            {/* Filter Panel */}
            {showFilters && (
                <div className="px-3 pb-3 pt-3">
                    <DatasetFilterPanel
                        onFilterChange={setFilterConfig}
                        initialFilters={filterConfig}
                        disabled={sourceId !== 'main' && sourceId !== 'pending' && !sourceId?.toString().includes('my-submissions')}
                        showStatusFilter={!!(metadata && metadata.isPending)}
                    />
                </div>
            )}

            {/* Export Toolbar (current filtered view, above the data preview) */}
            <div className="px-3 pb-2 d-flex justify-content-between align-items-center">
                <small className="text-muted">{displayData.length} record{displayData.length === 1 ? '' : 's'} in current view</small>
                <Dropdown>
                    <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        id="dataset-explorer-export-dropdown"
                        title="Export the current view"
                        disabled={displayData.length === 0}
                    >
                        <FaDownload className="me-1" /> Export
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={() => handleExport('csv')}>
                            <FaFileCsv className="me-2 text-success" /> CSV
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleExport('excel')}>
                            <FaFileExcel className="me-2 text-primary" /> Excel
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {/* Data Table */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="table-responsive bg-white rounded border mx-3 border shadow-sm flex-grow-1"
                style={{ maxHeight: '60vh', overflowY: 'auto', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.02)' }}
            >
                <table className="table table-hover table-striped mb-0 table-sm" style={{ fontSize: '0.9rem' }}>
                    <thead className="table-light sticky-top" style={{ zIndex: 10, top: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <tr>
                            {/* Manual Exclude Column Header */}
                            {enableVersioning && (
                                <th scope="col" className="px-3 py-3 border-bottom text-center" style={{ width: '50px', fontWeight: '600', color: '#4b5563' }}>
                                    <span title="Exclude from version">Excl.</span>
                                </th>
                            )}
                            {headers.map((key) => {
                                const sortable = key !== 'Actions' && key !== 'Images';
                                const isSorted = sortConfig.key === key;
                                return (
                                    <th
                                        key={key}
                                        scope="col"
                                        className="text-nowrap px-3 py-3 border-bottom"
                                        style={{ fontWeight: '600', color: '#4b5563', cursor: sortable ? 'pointer' : 'default', userSelect: 'none' }}
                                        onClick={sortable ? () => requestSort(key) : undefined}
                                        title={sortable ? `Sort by ${key}` : undefined}
                                    >
                                        {key}
                                        {sortable && (
                                            <span className="ms-1 text-muted">
                                                {isSorted
                                                    ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />)
                                                    : <FaSort style={{ opacity: 0.35 }} />}
                                            </span>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {displayData.length === 0 && (
                            <tr>
                                <td colSpan={headers.length + (enableVersioning ? 1 : 0)} className="text-center py-5 text-muted">
                                    <p className="mb-2">No records match your current filters.</p>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setFilterConfig({})}
                                    >
                                        <FaTimes className="me-1" /> Clear filters
                                    </button>
                                </td>
                            </tr>
                        )}
                        {displayData.slice(0, visibleCount).map((row, idx) => {
                            const recordId = row['Experiment Id'] || row.experimentId || row.recordId || row.id;
                            const isExcluded = excludedIds.has(recordId);
                            return (
                                <tr key={idx} style={isExcluded ? { opacity: 0.5, backgroundColor: '#f9fafb' } : {}}>
                                    {/* Manual Exclude Checkbox */}
                                    {enableVersioning && (
                                        <td className="px-3 py-2 text-center align-middle">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={isExcluded}
                                                onChange={() => toggleExclusion(recordId)}
                                                title="Exclude this record from the dataset version"
                                            />
                                        </td>
                                    )}
                                    {headers.map((key) => {
                                        if (key === 'Actions') {
                                            return (
                                                <td key={`${idx}-${key}`} className="px-3 py-2 text-nowrap text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => onEdit && onEdit(recordId)}
                                                        title="Edit Submission"
                                                        disabled={isExcluded}
                                                    >
                                                        <FaEdit /> Edit
                                                    </button>
                                                </td>
                                            );
                                        }
                                        if (key === 'Images') {
                                            return (
                                                <td key={`${idx}-${key}`} className="px-3 py-2 text-nowrap text-center">
                                                    <ReviewImage images={row[key] !== '-' ? row[key] : []} compact={true} />
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={`${idx}-${key}`} className="px-3 py-2 text-nowrap" style={{ color: '#1f2937' }}>
                                                {row[key]}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {visibleCount < displayData.length && (
                            <tr>
                                <td colSpan={headers.length + (enableVersioning ? 1 : 0)} className="text-center py-3 text-muted">
                                    <FaSpinner className="spinner-border spinner-border-sm me-2" /> Loading more rows...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2 px-3 pb-2 flex-shrink-0">
                <small className="text-muted">Showing {Math.min(visibleCount, displayData.length)} of {displayData.length} records</small>
            </div>

            {/* Save Modal */}
            {showSaveModal && ReactDOM.createPortal(
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1060 }} tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow-lg">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">Save Dataset Version</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowSaveModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p className="text-muted small mb-3">
                                        Save the current filter configuration to easily access this dataset subset later.
                                    </p>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Version Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g., 'My Dataset Subset'"
                                            value={versionName}
                                            onChange={(e) => setVersionName(e.target.value)}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="alert alert-info py-2 d-flex align-items-center" style={{ fontSize: '0.9rem' }}>
                                        <FaInfoCircle className="me-2 flex-shrink-0" />
                                        <div>
                                            This version will contain <strong>{displayData.length - excludedIds.size}</strong> records matching your current filters (minus {excludedIds.size} manually excluded).
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light" style={{ borderTop: '1px solid #dee2e6' }}>
                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowSaveModal(false)}>Cancel</button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm px-4"
                                        onClick={handleSaveVersion}
                                        disabled={saving}
                                    >
                                        {saving ? <div className="spinner-border spinner-border-sm me-2" role="status"></div> : <FaDownload className="me-2" />}
                                        Save Version
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export default DatasetExplorer;
